/**
 * Admin Users Page
 *
 * User management for administrators — view all users, change roles and
 * delete accounts.
 */

import { ErrorReason } from '@polygraf/shared'
import { AlertTriangle, Crown, Presentation, Search, Shield, Trash2, User } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useLocation } from 'react-router-dom'
import { Alert, Button, Modal, Select, Spinner, TextField } from '@/components/ui'
import { usePageTitle } from '@/hooks/use-page-title'
import { type AppRole, useUserRole } from '@/hooks/use-user-role'
import { useSession } from '@/lib/auth-client'
import { trpc } from '@/trpc/client'

const ROLE_CONFIG: Record<AppRole, { icon: typeof Shield; colorClass: string; bgClass: string }> = {
	ADMIN: { icon: Crown, colorClass: 'text-coral-700', bgClass: 'bg-error/10' },
	PRESENTER: { icon: Presentation, colorClass: 'text-coral-700', bgClass: 'bg-accent/10' },
	USER: { icon: User, colorClass: 'text-base-content/60', bgClass: 'bg-base-200' },
}

/**
 * The `alert-refined-*` colours are tuned for the light theme; on the dark
 * card they fall under readable contrast. Lift the text to the 300 shade
 * there (coral-300 / sage-300 on slate measure well over 4.5:1).
 */
const DARK_ERROR_TEXT = '[[data-theme=polygraf-dark]_&]:text-coral-300'
const DARK_SUCCESS_TEXT = '[[data-theme=polygraf-dark]_&]:text-sage-300'

/** How long the success toast stays on screen. */
const TOAST_MS = 6000

interface DeleteTarget {
	id: string
	name: string | null
	email: string
}

/**
 * Confirm dialog for an admin deleting someone else's account. Mirrors the
 * self-delete dialog on the profile page: it names every owned space the
 * cascade takes with the account (for all of that space's members), the live
 * sessions that end and the files that go, and asks for the target's e-mail
 * before the delete button unlocks.
 */
function DeleteUserDialog({
	target,
	onClose,
	onDeleted,
}: {
	target: DeleteTarget | null
	onClose: () => void
	onDeleted: (target: DeleteTarget) => void
}) {
	const { t } = useTranslation()
	const utils = trpc.useUtils()
	const [confirmEmail, setConfirmEmail] = useState('')
	const [error, setError] = useState<string | null>(null)

	// A fresh dialog for every target: the typed e-mail and any error from
	// the previous target (including one just deleted) must not carry over.
	const targetId = target?.id
	// biome-ignore lint/correctness/useExhaustiveDependencies: reset keyed on the target id
	useEffect(() => {
		setConfirmEmail('')
		setError(null)
	}, [targetId])

	// Always refetched on open: a space created since the last look must
	// still appear in the warning.
	const preview = trpc.users.adminDeletionPreview.useQuery(
		{ userId: target?.id ?? '' },
		{ enabled: target !== null, staleTime: 0, refetchOnMount: 'always', retry: 1 },
	)

	const deleteMutation = trpc.users.adminDelete.useMutation({
		onSuccess: () => {
			if (target) onDeleted(target)
		},
		onError: (err) => {
			const code = err.data?.code
			if (code === 'TOO_MANY_REQUESTS') {
				setError(t('admin.deleteUser.tooManyAttempts'))
			} else if (code === 'NOT_FOUND') {
				// Deleted meanwhile (another admin, or self-delete): drop the row.
				setError(t('admin.deleteUser.notFound'))
				utils.users.list.invalidate()
			} else if (code === 'CONFLICT') {
				setError(t('admin.deleteUser.lastAdmin'))
			} else if (code === 'FORBIDDEN') {
				setError(t('admin.deleteUser.forbidden'))
			} else if (err.data?.reason === ErrorReason.CONFIRM_EMAIL_MISMATCH) {
				setError(t('admin.deleteUser.emailMismatch'))
			} else {
				setError(t('admin.deleteUser.failed'))
			}
		},
	})
	const isDeleting = deleteMutation.isPending
	// A refetch (reopening the same user) still shows the previous preview;
	// hold the button until the fresh one lands, so the admin confirms
	// against what will actually be deleted.
	const isRefreshing = preview.isFetching

	const close = useCallback(() => {
		if (isDeleting) return
		setConfirmEmail('')
		setError(null)
		onClose()
	}, [isDeleting, onClose])

	const targetEmail = preview.data?.user.email ?? target?.email ?? ''
	const targetName = preview.data?.user.name || target?.name || targetEmail
	const ownedSpaces = preview.data?.ownedSpaces ?? []
	const activeLiveSessions = preview.data?.activeLiveSessions ?? []
	const uploadedFileCount = preview.data?.uploadedFileCount ?? 0
	const confirmed =
		targetEmail.length > 0 && confirmEmail.trim().toLowerCase() === targetEmail.trim().toLowerCase()

	const handleDelete = () => {
		if (!target || !confirmed || !preview.data || isRefreshing) return
		setError(null)
		deleteMutation.mutate({ userId: target.id, confirmEmail })
	}

	return (
		<Modal
			open={target !== null}
			onClose={close}
			size="md"
			persistent={isDeleting}
			closeLabel={t('common.close')}
			title={
				<span className="flex items-center gap-3">
					<span className="w-10 h-10 rounded-full bg-error/10 flex items-center justify-center shrink-0">
						<AlertTriangle size={20} className={`text-error ${DARK_ERROR_TEXT}`} aria-hidden />
					</span>
					<span>{t('admin.deleteUser.title')}</span>
				</span>
			}
			footer={
				<>
					<Button variant="ghost" onClick={close} disabled={isDeleting}>
						{t('common.cancel')}
					</Button>
					<Button
						variant="danger"
						onClick={handleDelete}
						disabled={!confirmed || !preview.data || isRefreshing}
						loading={isDeleting || isRefreshing}
						leadingIcon={!isDeleting && !isRefreshing && <Trash2 size={16} aria-hidden />}
					>
						{isDeleting ? t('admin.deleteUser.deleting') : t('admin.deleteUser.confirm')}
					</Button>
				</>
			}
		>
			<p className="text-base-content/70 mb-4">
				{t('admin.deleteUser.desc', { name: targetName, email: targetEmail })}
			</p>
			{error && (
				<Alert tone="error" className={`mb-4 ${DARK_ERROR_TEXT}`}>
					{error}
				</Alert>
			)}
			{preview.isPending ? (
				<div className="flex justify-center py-4">
					<Spinner size="sm" />
				</div>
			) : preview.isError ? (
				<Alert tone="error" className={DARK_ERROR_TEXT}>
					{preview.error.data?.code === 'NOT_FOUND'
						? t('admin.deleteUser.notFound')
						: t('admin.deleteUser.previewFailed')}
				</Alert>
			) : (
				<>
					{/* `Space.owner` cascades: each space listed here disappears with
					    its rooms and services for every member. Say so by name. */}
					{ownedSpaces.length > 0 && (
						<Alert tone="error" className={`mb-4 ${DARK_ERROR_TEXT}`}>
							<p className="font-medium mb-2">{t('admin.deleteUser.ownedSpacesWarning')}</p>
							<ul className="list-disc pl-5 space-y-1">
								{ownedSpaces.map((space) => (
									<li key={space.id}>
										<span className="font-medium">{space.name}</span>{' '}
										<span className="opacity-80">
											(
											{t('admin.deleteUser.ownedSpaceStats', {
												rooms: space.roomCount,
												members: space.memberCount,
											})}
											)
										</span>
									</li>
								))}
							</ul>
						</Alert>
					)}
					{activeLiveSessions.length > 0 && (
						<Alert tone="error" className={`mb-4 ${DARK_ERROR_TEXT}`}>
							<p className="font-medium mb-2">{t('admin.deleteUser.activeSessionsWarning')}</p>
							<ul className="list-disc pl-5 space-y-1">
								{activeLiveSessions.map((session) => (
									<li key={session.id}>{session.title}</li>
								))}
							</ul>
						</Alert>
					)}
					{uploadedFileCount > 0 && (
						<p className="text-sm text-base-content/70 mb-4">
							{t('admin.deleteUser.uploadedFilesNote', { count: uploadedFileCount })}
						</p>
					)}
					{ownedSpaces.length === 0 &&
						activeLiveSessions.length === 0 &&
						uploadedFileCount === 0 && (
							<p className="text-sm text-base-content/70 mb-4">
								{t('admin.deleteUser.nothingElse')}
							</p>
						)}
					<TextField
						id="admin-delete-confirm-email"
						// `text`, not `email`: browsers autofill e-mail fields despite
						// `autoComplete="off"`, which would turn the confirmation into
						// a single click.
						type="text"
						autoComplete="off"
						autoCapitalize="off"
						spellCheck={false}
						label={t('admin.deleteUser.confirmEmail', { email: targetEmail })}
						placeholder={targetEmail}
						value={confirmEmail}
						onChange={(e) => setConfirmEmail(e.target.value)}
						disabled={isDeleting}
					/>
				</>
			)}
		</Modal>
	)
}

export default function AdminUsersPage() {
	const { t } = useTranslation()
	const location = useLocation()
	const { isAdmin, isLoading: roleLoading, error: roleError } = useUserRole()
	usePageTitle(!roleLoading && !isAdmin ? t('admin.accessDenied') : t('admin.userManagement'))
	const { data: session } = useSession()
	const [search, setSearch] = useState('')
	// Screen-reader confirmation after a role change. The role <select> swaps to a
	// spinner on success, so a sighted user sees the new role land — but a SR user
	// gets no feedback without an explicit live-region announcement.
	const [roleUpdateAnnouncement, setRoleUpdateAnnouncement] = useState('')
	const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null)
	const [toast, setToast] = useState<string | null>(null)

	useEffect(() => {
		if (!toast) return
		const timer = window.setTimeout(() => setToast(null), TOAST_MS)
		return () => window.clearTimeout(timer)
	}, [toast])

	const closeDeleteDialog = useCallback(() => setDeleteTarget(null), [])

	const {
		data: users,
		isLoading,
		error,
	} = trpc.users.list.useQuery(undefined, {
		enabled: isAdmin,
		retry: false,
	})

	const utils = trpc.useUtils()

	const updateRoleMutation = trpc.users.updateRole.useMutation({
		onSuccess: (_data, variables) => {
			utils.users.list.invalidate()
			const updated = users?.find((u) => u.id === variables.userId)
			const name = updated?.name || updated?.email || variables.userId
			const roleLabel = t(`roles.${variables.role.toLowerCase()}`)
			// No dedicated "X is now <role>" key exists; compose "<name>: <role>" from
			// existing keys. Reads cleanly to a SR (e.g. "Jane Doe: Admin").
			setRoleUpdateAnnouncement(`${name}: ${roleLabel}`)
		},
	})

	if (roleLoading) {
		return (
			<div className="max-w-4xl mx-auto px-4 py-8 flex justify-center opacity-30">
				<Spinner size="lg" />
			</div>
		)
	}

	// If we couldn't determine the role at all, surface the error instead of
	// silently denying access — otherwise an admin sees "Access denied" on a
	// transient network blip and assumes the role was revoked.
	if (roleError) {
		// Anonymous users hit a 401 — redirect to sign-in with returnTo. Other
		// errors fall through to the generic alert.
		const code = (roleError.data as { code?: string } | null | undefined)?.code
		if (code === 'UNAUTHORIZED') {
			const returnTo = `${location.pathname}${location.search}`
			return <Navigate to={`/auth?returnTo=${encodeURIComponent(returnTo)}`} replace />
		}
		return (
			<div className="max-w-4xl mx-auto px-4 py-8">
				<Alert tone="error">{t('admin.roleCheckFailed')}</Alert>
			</div>
		)
	}

	if (!isAdmin) {
		return (
			<div className="max-w-4xl mx-auto px-4 py-8">
				<div className="card-refined p-12 text-center">
					<Shield size={48} className="mx-auto mb-4 opacity-30" aria-hidden />
					<p className="text-lg font-medium">{t('admin.accessDenied')}</p>
					<p className="text-base-content/60 mt-2">{t('admin.adminOnly')}</p>
				</div>
			</div>
		)
	}

	if (isLoading) {
		return (
			<div className="flex justify-center py-12 text-primary">
				<Spinner size="lg" />
			</div>
		)
	}

	if (error) {
		return (
			<div className="max-w-4xl mx-auto px-4 py-8">
				<Alert tone="error">{error.message}</Alert>
			</div>
		)
	}

	const filteredUsers =
		users?.filter((user) => {
			if (!search) return true
			const q = search.toLowerCase()
			return user.name?.toLowerCase().includes(q) || user.email.toLowerCase().includes(q)
		}) ?? []

	const handleDeleted = (deleted: DeleteTarget) => {
		setDeleteTarget(null)
		utils.users.list.invalidate()
		setToast(t('admin.deleteUser.success', { name: deleted.name || deleted.email }))
	}

	const handleRoleChange = (userId: string, newRole: AppRole) => {
		if (userId === session?.user?.id) return
		updateRoleMutation.mutate({ userId, role: newRole })
	}

	return (
		<div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
			<div>
				<h1 className="text-2xl font-semibold">{t('admin.userManagement')}</h1>
				<p className="text-base-content/60 mt-1">{t('admin.userManagementDesc')}</p>
			</div>

			{/* SR-only confirmation of the last role change. role="status" is the
			    intended live-region semantic here; <output> is the form-result element
			    and doesn't fit a standalone status announcement. */}
			{/* biome-ignore lint/a11y/useSemanticElements: status live region, not an <output> */}
			<span className="sr-only" role="status" aria-live="polite">
				{roleUpdateAnnouncement}
			</span>

			{/* Search */}
			<TextField
				aria-label={t('admin.searchUsers')}
				placeholder={t('admin.searchUsers')}
				value={search}
				onChange={(e) => setSearch(e.target.value)}
				leadingIcon={<Search size={18} aria-hidden />}
			/>

			{/* Role legend */}
			<div className="flex flex-wrap gap-3 text-sm">
				{(['ADMIN', 'PRESENTER', 'USER'] as const).map((r) => {
					const config = ROLE_CONFIG[r]
					const Icon = config.icon
					return (
						<span
							key={r}
							className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full ${config.bgClass} ${config.colorClass}`}
						>
							<Icon size={14} aria-hidden />
							{t(`roles.${r.toLowerCase()}`)}
						</span>
					)
				})}
			</div>

			{/* User list */}
			<div className="space-y-2">
				{filteredUsers.map((user) => {
					const role = (user.role as AppRole) ?? 'USER'
					const config = ROLE_CONFIG[role]
					const Icon = config.icon
					const isSelf = user.id === session?.user?.id

					return (
						<div key={user.id} className="card-refined p-4 flex items-center gap-4">
							<div
								className={`w-10 h-10 rounded-full flex items-center justify-center ${config.bgClass}`}
							>
								<Icon size={18} className={config.colorClass} aria-hidden />
							</div>

							<div className="flex-1 min-w-0">
								<div className="flex items-center gap-2">
									<span className="font-medium truncate">
										{user.name || user.email.split('@')[0]}
									</span>
									{isSelf && (
										<span className="px-1.5 py-0.5 text-[10px] rounded-full bg-primary/10 text-primary font-semibold">
											{t('admin.you')}
										</span>
									)}
								</div>
								<span className="text-sm text-base-content/60 truncate block">{user.email}</span>
							</div>

							<div className="flex items-center gap-2">
								{updateRoleMutation.isPending &&
								updateRoleMutation.variables?.userId === user.id ? (
									<span className="text-base-content/40">
										<Spinner size="sm" />
									</span>
								) : (
									<Select
										value={role}
										onChange={(e) => handleRoleChange(user.id, e.target.value as AppRole)}
										disabled={isSelf}
										className="text-sm py-1 pl-2 w-auto"
										block={false}
										aria-label={t('admin.changeRoleFor', { name: user.name ?? user.email })}
									>
										<option value="USER">{t('roles.user')}</option>
										<option value="PRESENTER">{t('roles.presenter')}</option>
										<option value="ADMIN">{t('roles.admin')}</option>
									</Select>
								)}
								{/* An admin deletes their own account from the profile page,
								    which asks for their password — never from here. */}
								{!isSelf && (
									<Button
										variant="ghost"
										size="sm"
										onClick={() =>
											setDeleteTarget({ id: user.id, name: user.name, email: user.email })
										}
										aria-label={t('admin.deleteUser.buttonFor', { name: user.name ?? user.email })}
										title={t('admin.deleteUser.button')}
										className={`text-coral-700 border border-error/30 hover:bg-error/10 ${DARK_ERROR_TEXT}`}
									>
										<Trash2 size={16} aria-hidden />
										<span className="hidden sm:inline">{t('admin.deleteUser.button')}</span>
									</Button>
								)}
							</div>
						</div>
					)
				})}
			</div>

			{filteredUsers.length === 0 && (
				<div className="card-refined p-8 text-center">
					<p className="text-base-content/60">{t('admin.noUsersFound')}</p>
				</div>
			)}

			{updateRoleMutation.error && (
				<Alert tone="error" className={`text-sm ${DARK_ERROR_TEXT}`}>
					{updateRoleMutation.error.data?.code === 'CONFLICT'
						? t('admin.lastAdminRole')
						: updateRoleMutation.error.message}
				</Alert>
			)}

			<DeleteUserDialog
				target={deleteTarget}
				onClose={closeDeleteDialog}
				onDeleted={handleDeleted}
			/>

			{/* Success toast. The card gives it an opaque surface in both
			    themes; the Alert inside is a role="status" live region. */}
			{toast && (
				<div className="fixed bottom-4 right-4 left-4 sm:left-auto z-50 sm:max-w-sm card-refined shadow-xl p-0 animate-enter">
					<Alert tone="success" className={DARK_SUCCESS_TEXT}>
						{toast}
					</Alert>
				</div>
			)}
		</div>
	)
}
